import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Paper,
    TextField,
    Typography,
} from "@material-ui/core";
import ordersService from "../../../services/orders.service";
import { currencyFormat } from "../../../helpers";

export default function SnappOrderActions({ info, onChanged, errorHandler }) {
    const [loading, setLoading] = useState(false);
    const [updateOpen, setUpdateOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [updateConfirmOpen, setUpdateConfirmOpen] = useState(false);
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [counts, setCounts] = useState({});
    const [now, setNow] = useState(Date.now());

    const isSnapp =
        info?.paymentProvider === "snapp" ||
        info?.transaction?.provider === "snapp";
    const locked = ["shipped", "completed", "cancelled"].includes(info?.status);

    useEffect(() => {
        setCounts(
            (info?.items || []).reduce(
                (result, item) => ({ ...result, [item.id]: item.count }),
                {}
            )
        );
    }, [info?.items]);

    const changed = useMemo(
        () =>
            (info?.items || []).some(
                (item) => Number(counts[item.id]) < Number(item.count)
            ),
        [counts, info?.items]
    );
    const remainingCount = useMemo(
        () =>
            (info?.items || []).reduce(
                (sum, item) => sum + Number(counts[item.id] ?? item.count),
                0
            ),
        [counts, info?.items]
    );
    const currentItemCount = useMemo(
        () =>
            (info?.items || []).reduce(
                (sum, item) => sum + Number(item.count || 0),
                0
            ),
        [info?.items]
    );
    const cannotUpdateLastItem = currentItemCount <= 1;
    const updateCooldownSeconds = useMemo(() => {
        const lastUpdate = info?.transaction?.lastSnappUpdateAt;
        if (!lastUpdate) return 0;
        return Math.max(
            0,
            Math.ceil((new Date(lastUpdate).getTime() + 30000 - now) / 1000)
        );
    }, [info?.transaction?.lastSnappUpdateAt, now]);

    useEffect(() => {
        setNow(Date.now());
    }, [info?.transaction?.lastSnappUpdateAt]);

    useEffect(() => {
        if (!updateCooldownSeconds) return undefined;
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [updateCooldownSeconds]);

    if (!isSnapp) return null;

    const refresh = async () => {
        const { data } = await ordersService.getOrder(info.id);
        if (data) onChanged(data);
    };

    const setItemCount = (item, count) => {
        setCounts((current) => ({
            ...current,
            [item.id]: Math.max(0, Math.min(Number(item.count), Number(count))),
        }));
    };

    const openUpdateDialog = () => {
        setReason("کاهش تعداد یا حذف آیتم توسط ادمین");
        setUpdateConfirmOpen(false);
        setCounts(
            (info?.items || []).reduce(
                (result, item) => ({ ...result, [item.id]: item.count }),
                {}
            )
        );
        setUpdateOpen(true);
    };

    const openCancelDialog = () => {
        setReason("لغو کامل سفارش توسط ادمین");
        setCancelConfirmOpen(false);
        setCancelOpen(true);
    };

    const openUpdateConfirmation = () => {
        setUpdateOpen(false);
        setUpdateConfirmOpen(true);
    };

    const closeUpdateConfirmation = () => {
        setUpdateConfirmOpen(false);
        setUpdateOpen(true);
    };

    const openCancelConfirmation = () => {
        setCancelOpen(false);
        setCancelConfirmOpen(true);
    };

    const closeCancelConfirmation = () => {
        setCancelConfirmOpen(false);
        setCancelOpen(true);
    };

    const checkStatus = async () => {
        setLoading(true);
        const { data, error } = await ordersService.getSnappStatus(info.id);
        setLoading(false);
        if (error) return errorHandler(error);
        await refresh();
        return data;
    };

    const submitUpdate = async () => {
        setLoading(true);
        const { error } = await ordersService.updateSnappOrder(info.id, {
            items: info.items.map((item) => ({
                orderItemId: item.id,
                count: Number(counts[item.id]),
            })),
            reason,
            confirmed: true,
        });
        setLoading(false);
        if (error) return errorHandler(error);
        setUpdateConfirmOpen(false);
        setUpdateOpen(false);
        setReason("");
        await refresh();
    };

    const submitCancel = async () => {
        setLoading(true);
        const { error } = await ordersService.cancelSnappOrder(info.id, {
            reason,
            goodsNotDispatched: true,
            confirmed: true,
        });
        setLoading(false);
        if (error) return errorHandler(error);
        setCancelConfirmOpen(false);
        setCancelOpen(false);
        setReason("");
        await refresh();
    };

    return (
        <>
            <Paper className="mt-2 mb-4 p-3" variant="outlined">
                <Typography variant="h6">مدیریت پرداخت اسنپ‌پی</Typography>
                <Typography>
                    وضعیت درگاه: {info?.transaction?.providerStatus || "نامشخص"}
                </Typography>
                {info?.transaction?.gatewayTransactionId && (
                    <Typography>
                        شناسه تراکنش اسنپ‌پی:{" "}
                        <span dir="ltr">{info.transaction.gatewayTransactionId}</span>
                    </Typography>
                )}
                <Typography>
                    مبلغ: {currencyFormat(info?.payableAmount || 0)}
                </Typography>
                <Grid container spacing={1} className="mt-2">
                    <Grid item>
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={loading}
                            onClick={checkStatus}
                        >
                            استعلام وضعیت
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={
                                loading ||
                                locked ||
                                cannotUpdateLastItem ||
                                updateCooldownSeconds > 0
                            }
                            onClick={openUpdateDialog}
                        >
                            {cannotUpdateLastItem
                                ? "امکان اصلاح آخرین کالا وجود ندارد"
                                : updateCooldownSeconds > 0
                                ? `امکان اصلاح تا ${updateCooldownSeconds} ثانیه دیگر`
                                : "اصلاح سفارش"}
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="secondary"
                            disabled={loading || locked}
                            onClick={openCancelDialog}
                        >
                            لغو و بازپرداخت
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Dialog open={updateOpen} onClose={() => setUpdateOpen(false)} fullWidth>
                <DialogTitle>اصلاح سفارش اسنپ‌پی</DialogTitle>
                <DialogContent>
                    {(info?.items || []).map((item) => {
                        const nextCount = Number(counts[item.id] ?? item.count);
                        return (
                            <Paper key={item.id} variant="outlined" className="p-2 mt-2">
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item xs={12} sm>
                                        <Typography>{item.name || item.category || "آیتم سفارش"}</Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            تعداد فعلی: {item.count}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            variant="outlined"
                                            disabled={nextCount <= 0}
                                            onClick={() => setItemCount(item, nextCount - 1)}
                                        >
                                            کاهش یک عدد
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            type="number"
                                            label="تعداد جدید"
                                            value={nextCount}
                                            inputProps={{ min: 0, max: item.count }}
                                            onChange={(event) =>
                                                setItemCount(item, event.target.value)
                                            }
                                            style={{ width: 110 }}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            color="secondary"
                                            variant="outlined"
                                            disabled={nextCount === 0}
                                            onClick={() => setItemCount(item, 0)}
                                        >
                                            حذف کامل آیتم
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        );
                    })}
                    {remainingCount === 0 && (
                        <Typography color="error" className="mt-2">
                            برای حذف همه آیتم‌ها از گزینه لغو کامل سفارش استفاده کنید.
                        </Typography>
                    )}
                    {updateCooldownSeconds > 0 && (
                        <Typography color="error" className="mt-2">
                            برای به‌روزرسانی مجدد باید {updateCooldownSeconds} ثانیه صبر کنید.
                        </Typography>
                    )}
                    <TextField
                        fullWidth
                        required
                        margin="normal"
                        label="دلیل اصلاح"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUpdateOpen(false)}>انصراف</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={
                            loading ||
                            !reason.trim() ||
                            !changed ||
                            remainingCount === 0 ||
                            updateCooldownSeconds > 0
                        }
                        onClick={openUpdateConfirmation}
                    >
                        ادامه و بررسی نهایی
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth>
                <DialogTitle>لغو کامل پرداخت اسنپ‌پی</DialogTitle>
                <DialogContent>
                    <Typography color="error">
                        این عملیات برگشت‌پذیر نیست و کل سفارش را لغو می‌کند.
                    </Typography>
                    <TextField
                        fullWidth
                        required
                        margin="normal"
                        label="دلیل لغو"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelOpen(false)}>انصراف</Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        disabled={loading || !reason.trim()}
                        onClick={openCancelConfirmation}
                    >
                        ادامه و بررسی نهایی
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={updateConfirmOpen}
                onClose={() => !loading && closeUpdateConfirmation()}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>تأیید نهایی اصلاح سفارش</DialogTitle>
                <DialogContent>
                    <Typography color="error">
                        این عملیات قابل بازگشت نیست. آیا از کاهش تعداد یا حذف آیتم‌های
                        انتخاب‌شده مطمئن هستید؟
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={loading}
                        onClick={closeUpdateConfirmation}
                    >
                        خیر، بازگشت
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={loading}
                        onClick={submitUpdate}
                    >
                        بله، اصلاح سفارش
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={cancelConfirmOpen}
                onClose={() => !loading && closeCancelConfirmation()}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>تأیید نهایی لغو سفارش</DialogTitle>
                <DialogContent>
                    <Typography color="error">
                        با تأیید این مرحله اعلام می‌کنید کالا ارسال نشده است. کل سفارش
                        در اسنپ‌پی لغو می‌شود و این عملیات قابل بازگشت نیست.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={loading}
                        onClick={closeCancelConfirmation}
                    >
                        خیر، بازگشت
                    </Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        disabled={loading}
                        onClick={submitCancel}
                    >
                        بله، لغو کامل سفارش
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
